/**
 * @module auth
 * @description JWT authentication with refresh tokens, sessions, and impersonation
 */

import type { ModuleManifest } from '@gzl10/nexus-sdk'
import type { SchedulesApi } from '../schedules/index.js'
import { getAuthSecret } from '@kernel/crypto/secret-resolver.js'
import { createAuthRoutes } from './auth.routes.js'
import { createAuthMiddlewares } from './auth.middleware.js'
import { authConfigEntity, refreshTokenEntity, authIdentitiesEntity } from './auth.entity.js'
// eslint-disable-next-line no-restricted-imports -- runtime/ needed until NEX-347 migrates entity-factory to http/
import { createEntityServiceAsync } from '../../runtime/entity-factory.js'
import { personalTokenEntity } from './auth.pat.entity.js'
import { authActions, impersonateAction } from './actions/index.js'
import { createAuthService } from './auth.service.js'
import { buildAuthConfig } from './auth.types.js'
import { consumeTokenCreateHook } from './auth-hooks.js'
import { createRateLimits } from './actions/helpers.js'

// Re-export types del módulo
export type { AuthConfig, RefreshToken, UserSession, AuthUserRecord, RequestInfo, AuthIdentity, LinkIdentityInput, CreatePersonalTokenInput, PersonalTokenScope, PersonalToken } from './auth.types.js'
// Re-export schemas para validación externa
export { loginSchema, registerSchema, createPersonalTokenSchema } from './auth.types.js'
export type { LoginInput, RegisterInput } from './auth.types.js'

export const authModule: ModuleManifest = {
  name: 'auth',
  label: { en: 'Authentication', es: 'Autenticación' },
  icon: 'mdi:lock-outline',
  description: { en: 'JWT authentication with refresh tokens, session management, and password security', es: 'Autenticación JWT con tokens de refresco, gestión de sesiones y seguridad de contraseñas' },
  type: 'core',
  category: 'identity',
  dependencies: ['logger', 'users', 'audit'],
  definitions: [
    authConfigEntity,
    refreshTokenEntity,
    authIdentitiesEntity,
    personalTokenEntity
  ],

  actions: authActions,

  pages: [
    {
      id: 'user-profile',
      label: { en: 'My Profile', es: 'Mi perfil' },
      icon: 'mdi:account-outline',
      type: 'custom',
      component: 'ProfilePage',
      standalone: true,
      order: 0
    }
  ],
  // migrate: usa migración generada desde definitions
  init: async (ctx) => {
    // Read config from SingleService (envMapping applied automatically)
    const configService = ctx.services.get<{ get: () => Promise<Record<string, unknown> | null> }>('auth_config')
    const row = await configService.get()
    const secret = getAuthSecret()
    const authConfig = buildAuthConfig(row, secret)
    authConfig.onTokenCreate = consumeTokenCreateHook()

    // Production safety check
    if (authConfig.skipRegisterOtp && process.env['NODE_ENV'] === 'production') {
      throw new Error('AUTH_SKIP_REGISTER_OTP cannot be enabled in production')
    }

    // Create and register auth service singleton
    const authService = createAuthService(ctx, authConfig)
    ctx.services.register('auth', authService)

    // Alias refresh-tokens entity service as 'refreshTokens' (engine pre-registers
    // under the table name `auth_refresh_tokens`; this alias keeps the legacy key).
    if (!ctx.services.has('refreshTokens')) {
      const tokensService = ctx.services.has(refreshTokenEntity.table)
        ? ctx.services.get(refreshTokenEntity.table)
        : await createEntityServiceAsync(ctx, refreshTokenEntity)
      ctx.services.register('refreshTokens', tokensService as never)
    }

    // Registrar middlewares para uso de otros módulos (shared ability cache)
    const { auth, optionalAuth } = createAuthMiddlewares(ctx, authConfig.secret)
    ctx.core.middleware['auth'] = auth
    ctx.core.middleware['optionalAuth'] = optionalAuth

    // Pre-create rate limits (sync) and register as service for action middlewares
    const rateLimits = createRateLimits(ctx, authConfig)
    ctx.services.register('authRateLimits', rateLimits)

    // Register authConfig for action handlers (getCookieOptions, etc.)
    ctx.services.register('authConfig', authConfig)

    // Register CAPTCHA capability for frontend widget
    ctx.capabilities.register('captcha', () => {
      if (authConfig.captchaProvider === 'none' || !authConfig.captchaSiteKey) return null
      return {
        provider: authConfig.captchaProvider,
        siteKey: authConfig.captchaSiteKey
      }
    })

    ctx.events.on('schedules.ready', async (...args: unknown[]) => {
      const schedules = args[0] as SchedulesApi
      schedules.registerFunction('auth:cleanup-expired', async () => {
        const now = new Date().toISOString()
        const deletedSessions = await ctx.db.knex('auth_refresh_tokens').where('expires_at', '<', now).delete()
        const deletedPATs = await ctx.db.knex('auth_personal_tokens').whereNotNull('expires_at').where('expires_at', '<', now).delete()
        return { deletedSessions, deletedPATs, cutoff: now }
      })
      await schedules.ensureSystemSchedule({
        name: '__system:cleanup-sessions',
        description: 'Purge expired refresh tokens and PATs',
        cron_expression: '0 2 * * *',
        function_key: 'auth:cleanup-expired'
      })
    })
  },
  // Custom routes only for entities that need special mounting (tokens, config)
  routes: createAuthRoutes,
  routePrefix: '/auth',

  injectActions: [
    { target: { module: 'users', entity: 'users' }, action: impersonateAction }
  ]
}
